import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Check, Zap, ShieldCheck, ArrowRight, MessageCircle, Building2 } from 'lucide-react'
import { fetchAvailablePlans, fetchBcvRate, PlanItem } from '@/services/billingApi'
import { getWhatsAppSalesUrl } from '@/utils/contactLinks'

const PROMO_TRIAL_PLAN: PlanItem = {
  id: 'trial-promo',
  code: 'trial',
  name: 'Empieza ya',
  priceCents: 0,
  monthlyMsgLimit: 400,
  creditsIncluded: 400,
  trialDays: 7,
  maxStores: 1,
  isDefault: true,
  isActive: true,
  features: {
    badge: '⚡ 7 Días Gratis',
    description: 'Promoción de lanzamiento Beta: 7 días de prueba gratis con 400 créditos para validar ventas reales.',
    catalogLimit: 20,
    vision: true,
    handoff: true,
  },
}

const FALLBACK_PLANS: PlanItem[] = [
  PROMO_TRIAL_PLAN,
  {
    id: 'ini-fb',
    code: 'inicio',
    name: 'Inicio',
    priceCents: 499,
    monthlyMsgLimit: 1000,
    creditsIncluded: 1000,
    trialDays: 0,
    maxStores: 1,
    isDefault: false,
    isActive: true,
    features: {
      launchOffer: { regularPriceCents: 1000, discountPercent: 50, months: 3 },
      badge: 'Para empezar',
      description: 'Para negocios que están comenzando: 1.000 créditos mensuales y catálogo de hasta 50 productos.',
      catalogLimit: 50,
      vision: true,
      handoff: true,
    },
  },
  {
    id: 'emp-fb',
    code: 'emprendedor',
    name: 'Emprendedor',
    priceCents: 1250,
    monthlyMsgLimit: 3000,
    creditsIncluded: 3000,
    trialDays: 0,
    maxStores: 1,
    isDefault: false,
    isActive: true,
    features: {
      launchOffer: { regularPriceCents: 2500, discountPercent: 50, months: 3 },
      badge: 'Popular',
      description: 'Ideal para tiendas y negocios en crecimiento con atención constante en WhatsApp.',
      catalogLimit: 100,
      vision: true,
      handoff: true,
    },
  },
  {
    id: 'com-fb',
    code: 'comercio',
    name: 'Comercio',
    priceCents: 2450,
    monthlyMsgLimit: 8000,
    creditsIncluded: 8000,
    trialDays: 0,
    maxStores: 2,
    isDefault: false,
    isActive: true,
    features: {
      launchOffer: { regularPriceCents: 4900, discountPercent: 50, months: 3 },
      badge: '⭐ Más Popular',
      description: 'El punto de equilibrio ideal: 8.000 créditos, atención en equipo, catálogo ilimitado y análisis de comprobantes.',
      catalogLimit: -1,
      vision: true,
      handoff: true,
      receiptAnalysis: true,
      multiAgent: true,
    },
  },
]

export default function PricingSection() {
  const [plans, setPlans] = useState<PlanItem[]>([])
  const [bcvRate, setBcvRate] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)
  const [activeIndex, setActiveIndex] = useState(0)
  const trackRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    let isMounted = true

    async function loadPricing() {
      try {
        const [plansData, bcvData] = await Promise.allSettled([
          fetchAvailablePlans(),
          fetchBcvRate(),
        ])

        if (!isMounted) return

        if (plansData.status === 'fulfilled' && plansData.value.length > 0) {
          setPlans(plansData.value)
        } else {
          setPlans(FALLBACK_PLANS)
        }

        if (bcvData.status === 'fulfilled' && bcvData.value?.rate > 0) {
          setBcvRate(bcvData.value.rate)
        }
      } catch (err) {
        if (isMounted) setPlans(FALLBACK_PLANS)
      } finally {
        if (isMounted) setLoading(false)
      }
    }

    loadPricing()

    return () => {
      isMounted = false
    }
  }, [])

  const basePlans = (loading ? FALLBACK_PLANS : plans).filter(
    (plan) => plan.code !== 'empresa'
  )
  const paidPlans = basePlans.filter((plan) => plan.code !== 'trial')
  const visiblePlans = [PROMO_TRIAL_PLAN, ...paidPlans]

  const handleScroll = () => {
    const track = trackRef.current
    if (!track) return
    const center = track.scrollLeft + track.clientWidth / 2
    let nearest = 0
    let nearestDistance = Number.POSITIVE_INFINITY
    Array.from(track.children).forEach((child, index) => {
      const card = child as HTMLElement
      const distance = Math.abs(card.offsetLeft + card.offsetWidth / 2 - center)
      if (distance < nearestDistance) {
        nearestDistance = distance
        nearest = index
      }
    })
    setActiveIndex(nearest)
  }

  const goToPlan = (index: number) => {
    const card = trackRef.current?.children[index]
    card?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' })
  }

  const formatPriceUsd = (priceCents: number) => {
    if (priceCents === 0) return '$0'
    const usd = priceCents / 100
    return Number.isInteger(usd) ? `$${usd}` : `$${usd.toFixed(2)}`
  }

  const formatPriceBs = (priceCents: number) => {
    if (!bcvRate || priceCents === 0) return null
    const usd = priceCents / 100
    const bs = usd * bcvRate
    return `Bs. ${bs.toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  }

  return (
    <section id="planes" className="py-20 lg:py-28 bg-gradient-to-b from-gray-50 to-white w-full scroll-mt-16">
      <div className="w-full px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-800 px-4 py-1.5 rounded-full text-xs sm:text-sm font-semibold mb-4 tracking-wide uppercase">
            <Zap className="w-4 h-4 text-emerald-600" />
            <span>Oferta de Lanzamiento · 50% OFF por 3 Meses</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-gray-900 tracking-tight mb-4">
            Planes diseñados para cada etapa de tu negocio
          </h2>
          <p className="text-lg text-gray-600">
            Accede por <strong>7 días gratuitos</strong> (400 créditos) durante el lanzamiento de la versión Beta y aprovecha el <strong>50% de descuento por 3 meses</strong> en todos nuestros planes. Pagos en Bolívares (VES) vía Pago Móvil y transferencia bancaria a tasa oficial BCV (más adelante PayPal, tarjeta de débito y Binance Pay).
          </p>

          {bcvRate && (
            <div className="mt-4 inline-flex items-center gap-2 text-xs font-medium text-gray-500 bg-white px-3 py-1.5 rounded-md border border-gray-200 shadow-sm">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
              Tasa Oficial BCV:{' '}
              <strong className="text-gray-800">
                Bs. {bcvRate.toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} / USD
              </strong>
            </div>
          )}

          <div className="mt-4 flex items-center justify-center">
            <div className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-200/80 text-emerald-900 px-4 py-2 rounded-xl text-xs font-medium shadow-sm">
              <span className="bg-emerald-600 text-white text-[10px] font-extrabold px-2 py-0.5 rounded-full uppercase tracking-wider shrink-0">
                PROMOCIÓN BETA
              </span>
              <span>
                7 días gratuitos de prueba con activación asistida + 50% de descuento durante tus primeros 3 meses.
              </span>
            </div>
          </div>
        </div>

        {/* Pricing Cards Grid */}
        {/* En móvil es un slider con ajuste por tarjeta; desde md es una grilla con la columna 'Empieza ya' + los 3 planes. */}
        <div
          ref={trackRef}
          onScroll={handleScroll}
          role="region"
          aria-label="Planes disponibles, desliza para ver más"
          tabIndex={0}
          className="relative flex snap-x snap-mandatory items-stretch gap-4 overflow-x-auto scroll-smooth -mx-4 px-4 pt-6 pb-4 md:mx-0 md:grid md:grid-cols-2 lg:grid-cols-4 md:gap-6 md:overflow-visible md:px-0 md:pt-0 md:pb-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {visiblePlans.map((plan) => {
            const isHighlighted = plan.code === 'comercio'
            const isTrial = plan.priceCents === 0 || plan.code === 'trial'
            const priceBs = formatPriceBs(plan.priceCents)
            const launchOffer = plan.features?.launchOffer
            const regularPriceUsd = launchOffer?.regularPriceCents
              ? formatPriceUsd(launchOffer.regularPriceCents)
              : null
            const badge = (plan.features as any)?.badge || (isHighlighted ? '⭐ Más Popular' : isTrial ? '⚡ 7 Días Gratis' : undefined)
            const rawDescription = (plan.features as any)?.description || ''
            const description = rawDescription.replace(/multi-?agente[s]?/gi, 'atención en equipo')

            return (
              <div
                key={plan.id}
                className={`relative w-[82%] shrink-0 snap-center rounded-2xl flex flex-col justify-between transition-all duration-300 sm:w-[60%] md:w-auto md:shrink ${
                  isHighlighted
                    ? 'bg-white border-2 border-indigo-500 shadow-xl shadow-indigo-100 lg:scale-105 z-10'
                    : isTrial
                    ? 'bg-emerald-50/40 border-2 border-emerald-400 shadow-md hover:shadow-lg'
                    : 'bg-white border border-gray-200 shadow-sm hover:shadow-md hover:border-gray-300'
                }`}
              >
                {/* Badge Top */}
                {badge && (
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                    <span
                      className={`whitespace-nowrap px-3.5 py-1 rounded-full text-xs font-bold tracking-wide shadow-sm uppercase ${
                        isHighlighted
                          ? 'bg-indigo-600 text-white ring-2 ring-indigo-300'
                          : isTrial
                          ? 'bg-emerald-600 text-white ring-2 ring-emerald-200'
                          : 'bg-gray-900 text-white'
                      }`}
                    >
                      {badge}
                    </span>
                  </div>
                )}

                <div className="p-6 sm:p-7 flex-1 flex flex-col">
                  {/* Plan Name & Desc */}
                  <div className="mb-5">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                    <p className="text-xs text-gray-500 min-h-[38px] leading-relaxed">
                      {description}
                    </p>
                  </div>

                  {/* Price */}
                  <div className="mb-6 pb-6 border-b border-gray-100">
                    {launchOffer && regularPriceUsd && (
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className="text-sm font-semibold text-gray-400 line-through">
                          {`${regularPriceUsd}/mes`}
                        </span>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-extrabold bg-emerald-100 text-emerald-800 uppercase tracking-wide">
                          {`${launchOffer.discountPercent}% OFF · ${launchOffer.months} meses`}
                        </span>
                      </div>
                    )}
                    <div className="flex items-baseline gap-1">
                      <span className="text-4xl font-extrabold text-gray-900 tracking-tight">
                        {formatPriceUsd(plan.priceCents)}
                      </span>
                      <span className="text-sm font-medium text-gray-500">
                        {isTrial ? '/ 7 días gratis' : '/ mes'}
                      </span>
                    </div>
                    {isTrial && (
                      <p className="text-[11px] font-semibold text-emerald-700 mt-1">
                        7 días de prueba gratis en Beta · Sin tarjeta
                      </p>
                    )}
                    {launchOffer && (
                      <p className="text-[11px] font-medium text-indigo-700 mt-1">
                        Oferta de lanzamiento por {launchOffer.months} meses · 7 días gratis en Beta
                      </p>
                    )}
                    {priceBs && (
                      <p className="text-xs font-semibold text-emerald-700 mt-1">
                        ≈ {priceBs} / mes
                      </p>
                    )}
                  </div>

                  {/* Credits Highlight Box */}
                  <div className={`p-3.5 rounded-xl mb-6 flex items-center gap-3 ${
                    isHighlighted ? 'bg-indigo-50 text-indigo-900' : 'bg-gray-50 text-gray-800'
                  }`}>
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                      isHighlighted ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-700'
                    }`}>
                      <Zap className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="text-sm font-bold">
                        {plan.creditsIncluded.toLocaleString()} Créditos IA
                      </div>
                      <div className="text-[11px] text-gray-500">
                        ~{plan.monthlyMsgLimit ? plan.monthlyMsgLimit.toLocaleString() : plan.creditsIncluded.toLocaleString()} mensajes
                      </div>
                    </div>
                  </div>

                  {/* Features List */}
                  <ul className="space-y-3 mb-8 text-xs text-gray-600 flex-1">
                    <li className="flex items-start gap-2.5">
                      <Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                      <span>
                        <strong>WhatsApp Oficial</strong> Cloud & Webhook
                      </span>
                    </li>
                    <li className="flex items-start gap-2.5">
                      <Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                      <span>
                        Catálogo:{' '}
                        <strong>
                          {(plan.features as any)?.catalogLimit === -1 || plan.features?.catalogLimit === -1
                            ? 'Productos ilimitados'
                            : `${(plan.features as any)?.catalogLimit || 50} productos`}
                        </strong>
                      </span>
                    </li>
                    <li className="flex items-start gap-2.5">
                      <Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                      <span>
                        {plan.code === 'trial' || plan.code === 'inicio' ? (
                          '1 usuario en tu equipo'
                        ) : plan.code === 'emprendedor' ? (
                          'Hasta 2 usuarios en tu equipo'
                        ) : (
                          <strong>Equipo ilimitado (cola compartida)</strong>
                        )}
                      </span>
                    </li>
                    <li className="flex items-start gap-2.5">
                      <Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                      <span>
                        Tiendas conectadas:{' '}
                        <strong>{plan.maxStores} {plan.maxStores === 1 ? 'sucursal' : 'sucursales'}</strong>
                      </span>
                    </li>
                    <li className="flex items-start gap-2.5">
                      <Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                      <span>
                        Reconocimiento inteligente de fotos de productos
                      </span>
                    </li>
                    {((plan.features as any)?.receiptAnalysis || isHighlighted) && (
                      <li className="flex items-start gap-2.5">
                        <Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                        <span className="font-medium text-emerald-800">
                          Validación visual de comprobantes de pago
                        </span>
                      </li>
                    )}
                    <li className="flex items-start gap-2.5">
                      <Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                      <span>
                        Escalado automático a humanos sin costo de créditos
                      </span>
                    </li>
                  </ul>
                </div>

                {/* Card CTA Button */}
                <div className="p-6 sm:p-7 pt-0">
                  <Link
                    to={`/register?plan=${plan.code}`}
                    className={`w-full py-3 px-4 rounded-xl font-bold text-xs sm:text-sm flex items-center justify-center gap-2 transition-all ${
                      isHighlighted
                        ? 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-md shadow-indigo-200'
                        : isTrial
                        ? 'bg-emerald-600 text-white hover:bg-emerald-700 shadow-md shadow-emerald-200'
                        : 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border border-indigo-200'
                    }`}
                  >
                    <span>
                      {isTrial ? 'Empieza ya · 7 Días Gratis' : `Elegir ${plan.name} · 7 Días Gratis`}
                    </span>
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            )
          })}
        </div>

        {/* Indicador del slider (solo móvil) */}
        <div className="mt-2 flex justify-center gap-1 md:hidden">
          {visiblePlans.map((plan, index) => (
            <button
              key={plan.id}
              type="button"
              onClick={() => goToPlan(index)}
              aria-label={`Ver plan ${plan.name}`}
              aria-current={index === activeIndex}
              className="flex h-8 w-8 items-center justify-center rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600"
            >
              <span
                className={`block h-2.5 rounded-full transition-all ${
                  index === activeIndex ? 'w-6 bg-indigo-600' : 'w-2.5 bg-gray-300'
                }`}
              />
            </button>
          ))}
        </div>

        {/* Plan Empresa — Contacto directo */}
        <div className="mt-10 bg-slate-900 text-white rounded-2xl p-6 sm:p-8 shadow-md flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex items-start sm:items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-indigo-500/20 border border-indigo-400/30 flex items-center justify-center text-indigo-300 shrink-0">
              <Building2 className="w-6 h-6" />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-extrabold text-white text-lg">Plan Empresa</h3>
                <span className="text-[10px] font-bold uppercase tracking-wider px-2.5 py-0.5 rounded-full bg-indigo-500/30 text-indigo-200 border border-indigo-400/30">
                  A medida
                </span>
              </div>
              <p className="text-xs sm:text-sm text-slate-300 max-w-2xl">
                ¿Operas múltiples sucursales, alto volumen de conversaciones o necesitas integraciones a medida con tu ERP/API? Contáctanos para conversar sobre tu operación y armar el plan ideal junto a ti.
              </p>
            </div>
          </div>
          <a
            href={getWhatsAppSalesUrl('Hola IridiaChat, me interesa conversar sobre el Plan Empresa para mi negocio.')}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full md:w-auto shrink-0 px-6 py-3 rounded-xl bg-white hover:bg-slate-100 text-slate-900 font-bold text-xs sm:text-sm transition-colors flex items-center justify-center gap-2 shadow-sm"
          >
            <MessageCircle className="w-4 h-4 text-emerald-600" />
            <span>Contactar por Plan Empresa</span>
          </a>
        </div>

        {/* Trust & Guarantees Banner */}
        <div className="mt-6 bg-white border border-gray-200 rounded-2xl p-6 sm:p-8 shadow-sm flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 shrink-0">
              <ShieldCheck className="w-6 h-6" />
            </div>
            <div>
              <h4 className="font-bold text-gray-900 text-base">¿Dudas sobre qué plan elegir o requieres un código de invitación?</h4>
              <p className="text-xs sm:text-sm text-gray-500">
                Por ahora aceptamos Pago Móvil y transferencia bancaria en Bolívares a tasa oficial BCV (más adelante habilitaremos PayPal, tarjeta de débito y Binance Pay). Chatea con un asesor para activar tus 7 días gratuitos o resolver dudas técnicas.
              </p>
            </div>
          </div>
          <a
            href={getWhatsAppSalesUrl('Hola IridiaChat, tengo dudas sobre los planes de precios y deseo solicitar asesoría comercial o un código de invitación.')}
            target="_blank"
            rel="noopener noreferrer"
            className="shrink-0 px-6 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-xs sm:text-sm transition-colors flex items-center gap-2 shadow-sm"
          >
            <MessageCircle className="w-4 h-4" />
            <span>Hablar con Asesor en WhatsApp</span>
          </a>
        </div>
      </div>
    </section>
  )
}
